import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import apiService from '@/api/service'
import type { LoginRequest } from '@/types/api'

// Demo key 和對應的預設帳號密碼
const DEMO_KEY = 'bo27fswj0a5pzjh3mz7gl2m26zwhmyrhhd5oxn9tttp9mofrge'
const DEMO_ACCOUNT = 'store0001'
const DEMO_PASSWORD = 'JOIHHIULGhogd845h3rgphbHOI980VHUJLG'

export const Login = (): JSX.Element => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [formData, setFormData] = useState<LoginRequest>({
    account: '',
    password: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string>('')

  const handleInputChange = (field: keyof LoginRequest) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }))
    // 清除錯誤訊息
    if (error) setError('')
  }

  // 執行登入的共用函數
  const performLogin = useCallback(async (loginData: LoginRequest) => {
    setIsLoading(true)
    setError('')

    try {
      const response = await apiService.login(loginData)

      if (response.status === 'success') {
        // 登入成功，顯示儲存的資料（調試用）
        console.log('登入成功！儲存的資料:')
        console.log('Store Code:', apiService.getCurrentStoreCode())
        console.log('Token:', apiService.getCurrentToken())
        console.log('Login Message:', apiService.getLoginMessage())
        console.log('Is Complete Data:', apiService.getIsCompleteData())
        console.log('Login Timestamp:', apiService.getLoginTimestamp())
        console.log('Complete Login Data:', apiService.getLoginData())

        // 檢查資料是否完整，決定跳轉目標
        if (response.isCompleteData === 'true' && apiService.isDataComplete()) {
          console.log('資料完整，跳轉到儀表板')
          navigate('/dashboard')
        } else {
          console.log('資料不完整，跳轉到店家設定頁面（首次設定）')
          // 傳遞 from: 'login' 參數，讓店家設定頁面顯示單個"完成設定"按鈕
          navigate('/store-setting', { state: { from: 'login' } })
        }
      } else {
        setError(response.message || '登入失敗')
        setIsLoading(false)
      }
    } catch (error) {
      console.error('Login error:', error)
      setError('登入失敗，請檢查帳號密碼是否正確')
      setIsLoading(false)
    }
  }, [navigate])

  // 檢查 URL 參數中的 key，如果匹配就自動登入
  useEffect(() => {
    const keyParam = searchParams.get('key')
    if (keyParam === DEMO_KEY) {
      console.log('檢測到 demo key，自動使用預設帳號登入')
      const demoLoginData: LoginRequest = {
        account: DEMO_ACCOUNT,
        password: DEMO_PASSWORD
      }
      // 更新表單顯示
      setFormData(demoLoginData)
      // 自動執行登入
      performLogin(demoLoginData)
    }
  }, [searchParams, performLogin])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // 驗證輸入
    if (!formData.account.trim()) {
      setError('請輸入帳號')
      return
    }
    if (!formData.password.trim()) {
      setError('請輸入密碼')
      return
    }

    // 使用共用登入函數
    await performLogin(formData)
  }
  return (
    <div
      className="bg-white w-full min-h-screen flex flex-col"
      data-model-id="1:2085"
    >
      {/* Header - 响应式 */}
      <div className="w-full h-14 md:h-16 relative bg-[#efdfac] border-b-2 [border-bottom-style:solid] border-white">
        <div className="absolute top-[9px] left-4 md:left-[34px] w-[25px] md:w-[30px] h-[35px] md:h-[42px] bg-[url(/vector-3.svg)] bg-[100%_100%]">
          <img
            className="absolute w-[36.79%] h-[26.20%] top-[23.52%] left-[31.27%]"
            alt="Vector"
            src="/vector-4.svg"
          />

          <img
            className="absolute w-[42.29%] h-[20.02%] top-[49.64%] left-[28.86%]"
            alt="Vector"
            src="/vector-2.svg"
          />
        </div>

        <div className="absolute top-[calc(50.00%_-_16px)] left-12 md:left-[74px] [font-family:'Noto_Sans',Helvetica] font-black text-white text-sm md:text-base text-center tracking-[1.20px] md:tracking-[1.60px] leading-4 whitespace-nowrap">
          CALLLINK
        </div>

        <div className="absolute top-[calc(50.00%_+_1px)] left-12 md:left-[74px] font-bold text-white text-[8px] md:text-[10px] tracking-[0.80px] md:tracking-[1.00px] leading-4 [font-family:'Noto_Sans_TC',Helvetica] text-left whitespace-nowrap">
          智慧語音客服雲平台
        </div>
      </div>

      {/* Main Content - 响应式 */}
      <div className="w-full flex-1 relative bg-[#aeceb7] overflow-hidden min-h-[600px] md:min-h-[976px] flex items-center justify-center px-4">
        <img
          className="absolute w-full h-full top-0 left-0 object-cover"
          alt="Bg pattern"
          src="/img/bg-pattern.png"
        />

        {/* 登入区域 - 响应式 */}
        <div className="absolute top-20 md:top-[233px] w-full max-w-[95%] sm:max-w-[500px] md:max-w-[800px] lg:max-w-[1200px] xl:max-w-[1450px] min-h-[500px] md:h-[748px] flex flex-col items-center bg-[#e8d080] rounded-[30px] md:rounded-[50px_50px_0px_0px] border-[3px] md:border-[5px] border-solid border-white py-8 md:py-0">
          {/* Logo - 响应式，手机端隐藏 */}
          <div className="hidden lg:block ml-[-534px] h-[167.97px] w-[120px] relative mt-[-130px] bg-[url(/vector.svg)] bg-[100%_100%]">
            <img
              className="absolute w-[36.79%] h-[26.20%] top-[23.52%] left-[31.27%]"
              alt="Vector"
              src="/vector-1.svg"
            />

            <img
              className="absolute w-[42.29%] h-[20.02%] top-[49.64%] left-[28.86%]"
              alt="Vector"
              src="/vector-2.svg"
            />
          </div>

          <div className="h-[23px] w-auto mt-6 md:mt-[127.0px] font-bold text-white text-base md:text-xl tracking-[2.00px] md:tracking-[4.00px] leading-[22.3px] [font-family:'Noto_Sans_TC',Helvetica] text-center px-4">
            請輸入您的帳號密碼
          </div>

          {/* 錯誤訊息 - 响应式 */}
          {error && (
            <div className="mt-4 px-4 py-2 bg-red-500 text-white rounded-lg text-xs md:text-sm mx-4 text-center">
              {error}
            </div>
          )}

          {/* 登入表單 - 响应式 */}
          <form onSubmit={handleSubmit} className="flex flex-col items-center w-full px-4">
            {/* 帳號輸入框 - 响应式 */}
            <div className="h-[50px] md:h-[55px] w-full max-w-[350px] md:max-w-[420px] mt-6 md:mt-[41px] flex items-center gap-2 md:gap-[9.5px] bg-white rounded-[50px] md:rounded-[373.22px] overflow-hidden relative z-10">
              <div className="h-[25px] md:h-[30px] w-[25px] md:w-[30px] relative ml-4 md:ml-[22px] flex-shrink-0">
                <div className="absolute w-[33.33%] h-[33.33%] top-[16.67%] left-[33.33%] bg-[#e2e2bd] rounded-[5px]" />

                <img
                  className="absolute w-[58.33%] h-[29.17%] top-[54.17%] left-[21.03%]"
                  alt="Rectangle"
                  src="/img/rectangle-4160.svg"
                />
              </div>

              <input
                type="text"
                value={formData.account}
                onChange={handleInputChange('account')}
                placeholder="輸入帳號"
                className="flex-1 h-full px-3 md:px-4 font-medium text-[#333] text-sm md:text-[16.8px] tracking-[1.5px] md:tracking-[3.35px] bg-transparent border-none outline-none placeholder:text-[#e2e2bd] relative z-20 cursor-text"
                disabled={isLoading}
              />
            </div>

            {/* 密碼輸入框 - 响应式 */}
            <div className="h-[50px] md:h-[55px] w-full max-w-[350px] md:max-w-[420px] mt-6 md:mt-[47px] flex items-center gap-2 md:gap-[7px] bg-white rounded-[50px] md:rounded-[373.22px] overflow-hidden relative z-10">
              <img
                className="h-[28px] md:h-[33.52px] w-[28px] md:w-[33.52px] ml-4 md:ml-[21.0px] flex-shrink-0"
                alt="Lock fill"
                src="/img/lock-fill.svg"
              />

              <input
                type="password"
                value={formData.password}
                onChange={handleInputChange('password')}
                placeholder="輸入密碼"
                className="flex-1 h-full px-3 md:px-4 font-medium text-[#333] text-sm md:text-[16.8px] tracking-[1.5px] md:tracking-[3.35px] bg-transparent border-none outline-none placeholder:text-[#e2e2bd] relative z-20 cursor-text"
                disabled={isLoading}
              />
            </div>

            {/* 登入按鈕 - 响应式 */}
            <button
              type="submit"
              disabled={isLoading}
              className={`h-[50px] md:h-[55px] w-full max-w-[350px] md:max-w-[420px] mt-6 md:mt-[47px] flex items-center justify-center rounded-[50px] md:rounded-[267.22px] overflow-hidden transition-colors ${isLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#929084] hover:bg-[#7a7a6b]'
                }`}
            >
              <div className="font-bold text-white text-lg md:text-2xl tracking-[6.00px] md:tracking-[24.00px] leading-4 [font-family:'Noto_Sans_TC',Helvetica] text-center whitespace-nowrap">
                {isLoading ? '登入中...' : '登入'}
              </div>
            </button>
          </form>
        </div>

        {/* 歡迎登入標題 - 响应式 */}
        <div className="absolute top-20 md:top-40 left-1/2 -translate-x-1/2 font-black text-white text-2xl md:text-4xl tracking-[3.60px] md:tracking-[7.20px] leading-9 [font-family:'Noto_Sans_TC',Helvetica] text-center whitespace-nowrap px-4">
          歡迎登入
        </div>
      </div>
    </div>
  );
};